import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../../../../../components/Modal';
import CloseButton from '../../../../../../components/CloseButton/CloseButton';
import styles from './UploadLogoModal.module.scss';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../../../../components/Button/Button';
import Spinner from '../../../../../../../common/components/Spinner';
import { useDropzone } from 'react-dropzone';
import { ToastNotification } from '../../../../../../components/Toast/Toast';
import VideoService from '../../../../../../service/VideoService';
import Icon from '../../../../../../components/Icon/Icon';
import { useUserContext } from '../../../../../../context/UserContext';

const MAX_WIDTH = 512; // value in px
const MAX_HEIGHT = 512; // value in px

const DEFAULT_LOGO_SETTINGS = {
  position: 'tr',
  size: 20,
  marginX: 2,
  marginY: 2,
};

const UploadLogoModal = ({ isOpen, onClose, onUpload, logoId, companyId }) => {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);

  const { updateUserAppConfig } = useUserContext();

  const { t } = useTranslation();

  const onDrop = useCallback(async ([acceptedFile]) => {
    if (acceptedFile) {
      const URLObj = window.URL || webkitURL;
      // Creates a DOMString containing a URL representing the object given in the parameter, namely the original Blob
      const blobLogoUrl = URLObj.createObjectURL(acceptedFile);

      const imageInfo = await new Promise(resolve => {
        const image = new Image();
        image.src = blobLogoUrl;

        image.onload = () => {
          resolve({ width: image.width, height: image.height });
        };
      });

      if (imageInfo.width > MAX_WIDTH || imageInfo > MAX_HEIGHT) {
        ToastNotification({
          message: t('company.settings.logos.create.size_error'),
        });
        return;
      }

      // If the logoId is set, we should only pass the acceptedFile and not upload the image directly to backend in this modal
      if (logoId) {
        onUpload({ image: acceptedFile, temporaryUrl: blobLogoUrl });
        onClose();
      } else {
        setImageUrl(blobLogoUrl);
      }

      setFiles([acceptedFile]);
      return;
    }

    ToastNotification({
      message: `${t('clip_organizer.add_modal.wrong_file_format')} ${t('company.settings.logos.create.available_formats')}`,
    });
  }, []);

  useEffect(() => {
    const uploadVideo = async (file, index) => {
      try {
        // Send a message to the backend to start uploading
        const url = await VideoService.uploadLogo(file);
        const payload = { ...DEFAULT_LOGO_SETTINGS, filePath: url };
        const { id } = await VideoService.createLogo(payload, companyId);

        updateUserAppConfig();

        ToastNotification({
          message: t('company.settings.logos.create.success'),
          iconName: 'success',
        });

        onUpload(id);
        onClose();
      } catch (err) {
        console.error('upload logo error', err);
        ToastNotification({
          message: `${t('company.settings.logos.create.error')}`,
        });

        setUploading(false);
        setImageUrl(null);
        setFiles([]);
      }
    };

    const uploadSeveralFiles = async () => {
      try {
        await Promise.all(
          files.map(async (file, index) => {
            return await uploadVideo(file, index);
          })
        );
      } catch (e) {
        console.log('Several videos upload failed', e);
      }
    };

    if (uploading && files.length > 0) {
      uploadSeveralFiles();
    }
  }, [files, uploading]);

  useEffect(() => {
    if (!isOpen) {
      setUploading(false);
      setImageUrl(null);
      setFiles([]);
    }
  }, [isOpen]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/jpg': [],
    },
    disabled: uploading,
  });

  return (
    <Modal open={isOpen} onClose={onClose} className={styles.modal}>
      <div className={styles.modalContainer}>
        <div className={styles.headerModal}>
          <span className={styles.modalTitle}>{t('company.settings.logos.create.upload_logo')}</span>
          <CloseButton onClose={onClose} />
        </div>
        <div {...getRootProps()} className={styles.dropzoneWrapper}>
          <input {...getInputProps()} accept="image/jpeg, image/png, image/jpg" />
          {uploading ? (
            <div className={styles.explanationContainer}>
              <Spinner width="40px" height="40px" size="3px" className={styles.processingSpinner} />
            </div>
          ) : (
            <div className={styles.explanationContainer}>
              {imageUrl ? (
                <div className={styles.logoPreviewContainer}>
                  <img src={imageUrl} className={styles.logoPreview} alt={`${t('logo_chooser.header')} ${t('company.settings.logos.update.preview')}`} />
                </div>
              ) : (
                <>
                  <h3 className={styles.header}>{t('company.settings.logos.create.drag_and_drop.title')}</h3>
                  <p className={styles.uploadingText}>{t('company.settings.logos.create.drag_and_drop.text')}</p>
                  {!isDragActive && (
                    <CustomButton variant="secondary" className={styles.browseBtn}>
                      <Icon src="search" className={styles.searchIconContainer} />
                      <span>{t('company.settings.logos.create.drag_and_drop.button')}</span>
                    </CustomButton>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <CustomButton variant="secondary" label={t('general.cancel')} className={styles.footerBtn} onClick={onClose} />
          {!logoId && (
            <CustomButton
              variant="primary"
              label={t('general.save')}
              className={styles.footerBtn}
              onClick={() => setUploading(true)}
              disabled={uploading || !files.length}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UploadLogoModal;
